import { supabase } from './supabase';

// Type definitions
export interface EmergencyProfile {
  id: string;
  user_id: string;
  type: 'personal' | 'household' | 'business';
  status: 'draft' | 'active' | 'archived';
  locale: string;
  name: string;
  description?: string;
  settings?: Record<string, any>;
  created_at: string;
  updated_at: string;
  activated_at?: string;
  archived_at?: string;
}

export interface EmergencyPerson {
  id: string;
  profile_id: string;
  name: string;
  date_of_birth?: string;
  relationship?: string;
  address?: string;
  allergies?: string[];
  medical_conditions?: string[];
  medications?: string[];
  gp_name?: string;
  gp_phone?: string;
  gp_address?: string;
  emergency_contact_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface EmergencyContact {
  id: string;
  profile_id: string;
  name: string;
  relationship?: string;
  role: 'primary_contact' | 'backup' | 'neighbour' | 'gp' | 'school' | 'insurer' | 'employer' | 'pet_carer';
  email?: string;
  phone: string;
  phone_secondary?: string;
  address?: string;
  notification_preferences?: Record<string, any>;
  access_rules?: Record<string, any>;
  is_primary: boolean;
  is_backup: boolean;
  created_at: string;
  updated_at: string;
}

export interface HazardPlan {
  id: string;
  profile_id: string;
  hazard_type: 'medical_emergency' | 'fire_bushfire' | 'flood_storm' | 'heatwave' | 'cyber_compromise' | 'missing_person' | 'travel_emergency' | 'pet_emergency';
  signals?: string[];
  first_60_minutes?: string[];
  notify_list?: string[];
  pack_refs?: string[];
  documents?: string[];
  notes?: string;
  last_reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface EmergencyPack {
  id: string;
  profile_id: string;
  name: string;
  pack_type: 'medical' | 'property_claim' | 'travel' | 'child_school' | 'sme_first_48h';
  description?: string;
  item_refs?: any[];
  permissions?: {
    readOnly: boolean;
    expiryAt?: string;
    recipientIds?: string[];
  };
  share_token?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface EmergencyCard {
  id: string;
  profile_id: string;
  person_id?: string;
  fields: {
    name?: boolean;
    dateOfBirth?: boolean;
    allergies?: boolean;
    medications?: boolean;
    decisionMakerContact?: boolean;
  };
  qr_token: string;
  wallet_pass_url?: string;
  lock_screen_url?: string;
  status: 'active' | 'revoked' | 'expired';
  revoked_at?: string;
  created_at: string;
  updated_at: string;
}

export interface EmergencyDrill {
  id: string;
  profile_id: string;
  hazard_type: string;
  started_at: string;
  completed_at?: string;
  time_to_find?: number;
  gaps_identified?: string[];
  notes?: string;
  metrics?: Record<string, any>;
  created_at: string;
}

export class EmergencyService {
  // Emergency Profiles
  static async getProfiles(userId: string): Promise<EmergencyProfile[]> {
    const { data, error } = await supabase
      .from('emergency_profiles')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async getActiveProfile(userId: string): Promise<EmergencyProfile | null> {
    const { data, error } = await supabase
      .from('emergency_profiles')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  static async createProfile(profile: Omit<EmergencyProfile, 'id' | 'created_at' | 'updated_at'>): Promise<EmergencyProfile> {
    const { data, error } = await supabase
      .from('emergency_profiles')
      .insert(profile)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateProfile(id: string, updates: Partial<EmergencyProfile>): Promise<EmergencyProfile> {
    const { data, error } = await supabase
      .from('emergency_profiles')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async activateProfile(id: string): Promise<EmergencyProfile> {
    const { data, error } = await supabase
      .from('emergency_profiles')
      .update({ 
        status: 'active',
        activated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteProfile(id: string): Promise<void> {
    const { error } = await supabase
      .from('emergency_profiles')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Emergency Persons
  static async getPersons(profileId: string): Promise<EmergencyPerson[]> {
    const { data, error } = await supabase
      .from('emergency_persons')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async createPerson(person: Omit<EmergencyPerson, 'id' | 'created_at' | 'updated_at'>): Promise<EmergencyPerson> {
    const { data, error } = await supabase
      .from('emergency_persons')
      .insert(person)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updatePerson(id: string, updates: Partial<EmergencyPerson>): Promise<EmergencyPerson> {
    const { data, error } = await supabase
      .from('emergency_persons')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deletePerson(id: string): Promise<void> {
    const { error } = await supabase
      .from('emergency_persons')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Emergency Contacts
  static async getContacts(profileId: string): Promise<EmergencyContact[]> {
    const { data, error } = await supabase
      .from('emergency_contacts')
      .select('*')
      .eq('profile_id', profileId)
      .order('is_primary', { ascending: false })
      .order('is_backup', { ascending: false })
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async createContact(contact: Omit<EmergencyContact, 'id' | 'created_at' | 'updated_at'>): Promise<EmergencyContact> {
    const { data, error } = await supabase
      .from('emergency_contacts')
      .insert(contact)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateContact(id: string, updates: Partial<EmergencyContact>): Promise<EmergencyContact> {
    const { data, error } = await supabase
      .from('emergency_contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteContact(id: string): Promise<void> {
    const { error } = await supabase
      .from('emergency_contacts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Hazard Plans
  static async getHazardPlans(profileId: string): Promise<HazardPlan[]> {
    const { data, error } = await supabase
      .from('emergency_hazard_plans')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async getHazardPlan(profileId: string, hazardType: string): Promise<HazardPlan | null> {
    const { data, error } = await supabase
      .from('emergency_hazard_plans')
      .select('*')
      .eq('profile_id', profileId)
      .eq('hazard_type', hazardType)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  static async createHazardPlan(plan: Omit<HazardPlan, 'id' | 'created_at' | 'updated_at'>): Promise<HazardPlan> {
    const { data, error } = await supabase
      .from('emergency_hazard_plans')
      .insert(plan)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateHazardPlan(id: string, updates: Partial<HazardPlan>): Promise<HazardPlan> {
    const { data, error } = await supabase
      .from('emergency_hazard_plans')
      .update({
        ...updates,
        last_reviewed_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteHazardPlan(id: string): Promise<void> {
    const { error } = await supabase
      .from('emergency_hazard_plans')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Emergency Packs
  static async getPacks(profileId: string): Promise<EmergencyPack[]> {
    const { data, error } = await supabase
      .from('emergency_packs')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async createPack(pack: Omit<EmergencyPack, 'id' | 'created_at' | 'updated_at' | 'share_token'>): Promise<EmergencyPack> {
    // Generate unique share token
    const shareToken = `ep_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    
    const { data, error } = await supabase
      .from('emergency_packs')
      .insert({
        ...pack,
        share_token: shareToken
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updatePack(id: string, updates: Partial<EmergencyPack>): Promise<EmergencyPack> {
    const { data, error } = await supabase
      .from('emergency_packs')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async sharePack(id: string, recipientIds: string[], expiresAt?: string): Promise<EmergencyPack> {
    const pack = await this.getPack(id);
    if (!pack) throw new Error('Pack not found');

    const { data, error } = await supabase
      .from('emergency_packs')
      .update({
        permissions: {
          ...pack.permissions,
          recipientIds,
          expiryAt: expiresAt
        },
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async getPack(id: string): Promise<EmergencyPack | null> {
    const { data, error } = await supabase
      .from('emergency_packs')
      .select('*')
      .eq('id', id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  static async getPackByToken(token: string): Promise<EmergencyPack | null> {
    const { data, error } = await supabase
      .from('emergency_packs')
      .select('*')
      .eq('share_token', token)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    
    // Check if expired
    if (data && data.expires_at && new Date(data.expires_at) < new Date()) {
      return null;
    }
    
    return data;
  }

  static async deletePack(id: string): Promise<void> {
    const { error } = await supabase
      .from('emergency_packs')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Emergency Cards
  static async getEmergencyCards(profileId: string): Promise<EmergencyCard[]> {
    const { data, error } = await supabase
      .from('emergency_cards')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async createEmergencyCard(card: Omit<EmergencyCard, 'id' | 'created_at' | 'updated_at' | 'qr_token' | 'status'>): Promise<EmergencyCard> {
    // Generate unique QR token
    const qrToken = `ec_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    
    const { data, error } = await supabase
      .from('emergency_cards')
      .insert({
        ...card,
        qr_token: qrToken,
        status: 'active'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async revokeEmergencyCard(id: string): Promise<EmergencyCard> {
    const { data, error } = await supabase
      .from('emergency_cards')
      .update({
        status: 'revoked',
        revoked_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async getEmergencyCardByToken(token: string): Promise<EmergencyCard | null> {
    const { data, error } = await supabase
      .from('emergency_cards')
      .select('*')
      .eq('qr_token', token)
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Drills
  static async createDrill(drill: Omit<EmergencyDrill, 'id' | 'created_at' | 'started_at'>): Promise<EmergencyDrill> {
    const { data, error } = await supabase
      .from('emergency_drills')
      .insert({
        ...drill,
        started_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async completeDrill(id: string, timeToFind: number, gaps: string[], notes?: string): Promise<EmergencyDrill> {
    const { data, error } = await supabase
      .from('emergency_drills')
      .update({
        completed_at: new Date().toISOString(),
        time_to_find: timeToFind,
        gaps_identified: gaps,
        notes
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async getDrills(profileId: string): Promise<EmergencyDrill[]> {
    const { data, error } = await supabase
      .from('emergency_drills')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Notifications (placeholder - would integrate with notification service)
  static async notifyContacts(contactIds: string[], packId: string, channels: string[]): Promise<void> {
    // This would integrate with the notification system
    // For now, just log the action
    console.log('Notifying contacts:', contactIds, 'about pack:', packId, 'via:', channels);
  }

  // Offline Cache
  static async updateOfflineCache(profileId: string, cacheData: any, deviceId?: string): Promise<void> {
    const { error } = await supabase
      .from('emergency_offline_cache')
      .upsert({
        profile_id: profileId,
        cache_data: cacheData,
        device_id: deviceId,
        last_updated: new Date().toISOString(),
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
      }, {
        onConflict: 'profile_id'
      });

    if (error) throw error;
  }

  static async getOfflineCache(profileId: string): Promise<any | null> {
    const { data, error } = await supabase
      .from('emergency_offline_cache')
      .select('*')
      .eq('profile_id', profileId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    
    if (data && data.expires_at && new Date(data.expires_at) < new Date()) {
      return null; // Cache expired
    }
    
    return data?.cache_data || null;
  }

  // Audit Log
  static async logAction(
    profileId: string,
    action: string,
    targetType?: string,
    targetId?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    const { error } = await supabase
      .from('emergency_audit_log')
      .insert({
        profile_id: profileId,
        actor_id: (await supabase.auth.getUser()).data.user?.id,
        action,
        target_type: targetType,
        target_id: targetId,
        metadata: metadata || {},
        timestamp: new Date().toISOString()
      });

    if (error) throw error;
  }
}

